import { getErrorMessage } from '../utils/errorHandler';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { 
  Plus, 
  Pencil,
  UserCheck,
  UserX,
  Key,
  Palette,
  Globe,
  Clock,
  Cloud,
  Newspaper,
  Trophy,
  Calendar,
  Copy,
  MessageSquare,
  Calculator
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USER_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
  '#14B8A6', '#F43F5E', '#A855F7', '#22C55E', '#0EA5E9',
  '#D946EF', '#FCD34D', '#FB923C', '#4ADE80', '#38BDF8',
  '#C084FC', '#FB7185', '#34D399', '#FBBF24', '#818CF8'
];

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' }
];

export default function Users() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneSourceUserId, setCloneSourceUserId] = useState('');
  const [cloneStartDate, setCloneStartDate] = useState('');
  const [cloneOverrides, setCloneOverrides] = useState({ role: '', weekly_contractual_hours: '', color: '', comments: '' });
  const [cloneLoading, setCloneLoading] = useState(false);
  // Simulation initial balance state
  const [simDialogOpen, setSimDialogOpen] = useState(false);
  const [simUserId, setSimUserId] = useState('');
  const [simStartDate, setSimStartDate] = useState('');
  const [simResult, setSimResult] = useState(null);
  const [simLoading, setSimLoading] = useState(false);
  const [applying, setApplying] = useState({ hours: false, leave: false });
  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    first_name: '',
    last_name: '',
    role: 'collaborateur',
    color: '#3B82F6',
    default_language: 'fr',
    weekly_contractual_hours: 40,
    default_leave_days: 25,
    show_weather: true,
    show_news_altstaetten: true,
    show_tennis: true,
    comments: '',
    hire_date: '',
    departure_date: ''
  });

  const ROLE_LABELS = {
    collaborateur: { label: t('collaborator'), class: 'bg-blue-500' },
    manager: { label: t('manager'), class: 'bg-purple-500' },
    directeur: { label: t('director'), class: 'bg-amber-500' },
  };

  const fetchUsers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/users`);
      setUsers(res.data);
    } catch (err) {
      toast.error(t('error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreateUser = async () => {
    // Validation: weekly_contractual_hours obligatoire
    if (!newUser.weekly_contractual_hours || newUser.weekly_contractual_hours <= 0) {
      toast.error(t('weeklyContractualHoursRequired') || 'Les heures contractuelles hebdomadaires sont obligatoires');
      return;
    }
    try {
      await axios.post(`${API}/users`, newUser);
      toast.success(t('userCreated'));
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;
    // Validation: weekly_contractual_hours obligatoire
    if (!newUser.weekly_contractual_hours || newUser.weekly_contractual_hours <= 0) {
      toast.error(t('weeklyContractualHoursRequired') || 'Les heures contractuelles hebdomadaires sont obligatoires');
      return;
    }
    try {
      await axios.put(`${API}/users/${editingUser.id}`, {
        first_name: newUser.first_name,
        last_name: newUser.last_name,
        email: newUser.email,
        is_active: newUser.is_active,
        color: newUser.color,
        default_language: newUser.default_language,
        weekly_contractual_hours: newUser.weekly_contractual_hours,
        default_leave_days: newUser.default_leave_days,
        role: newUser.role,
        show_weather: newUser.show_weather,
        show_news_altstaetten: newUser.show_news_altstaetten,
        show_tennis: newUser.show_tennis,
        comments: newUser.comments,
        hire_date: newUser.hire_date || null,
        departure_date: newUser.departure_date || null
      });
      toast.success(t('userUpdated'));
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const handleToggleActive = async (targetUser) => {
    try {
      await axios.put(`${API}/users/${targetUser.id}`, {
        is_active: !targetUser.is_active
      });
      toast.success(targetUser.is_active ? t('userDeactivated') : t('userActivated'));
      fetchUsers();
    } catch (err) {
      toast.error(t('error'));
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUserForPassword || !newPassword) return;
    try {
      // SECURITY FIX: Send password in POST body, not URL query params
      await axios.post(`${API}/users/${selectedUserForPassword.id}/password`, {
        new_password: newPassword
      });
      toast.success(t('passwordChanged'));
      setPasswordDialogOpen(false);
      setSelectedUserForPassword(null);
      setNewPassword('');
    } catch (err) {
      toast.error(getErrorMessage(err, t('error')));
    }
  };

  const openEditDialog = (targetUser) => {
    setEditingUser(targetUser);
    setNewUser({
      email: targetUser.email,
      password: '',
      first_name: targetUser.first_name,
      last_name: targetUser.last_name,
      role: targetUser.role,
      is_active: targetUser.is_active,
      color: targetUser.color || '#3B82F6',
      default_language: targetUser.default_language || 'fr',
      weekly_contractual_hours: targetUser.weekly_contractual_hours || 40,
      default_leave_days: targetUser.default_leave_days || 25,
      show_weather: targetUser.show_weather !== false,
      show_news_altstaetten: targetUser.show_news_altstaetten !== false,
      show_tennis: targetUser.show_tennis !== false,
      comments: targetUser.comments || '',
      hire_date: targetUser.hire_date || '',
      departure_date: targetUser.departure_date || ''
    });
    setDialogOpen(true);
  };

  const openPasswordDialog = (targetUser) => {
    setSelectedUserForPassword(targetUser);
    setNewPassword('');
    setPasswordDialogOpen(true);
  };

  const resetForm = () => {
    setEditingUser(null);
    setNewUser({
      email: '',
      password: '',
      first_name: '',
      last_name: '',
      role: 'collaborateur',
      color: '#3B82F6',
      default_language: 'fr',
      weekly_contractual_hours: 40,
      default_leave_days: 25,
      show_weather: true,
      show_news_altstaetten: true,
      show_tennis: true,
      comments: '',
      hire_date: '',
      departure_date: ''
    });
  };

  const openCloneDialog = () => {
    setCloneSourceUserId('');
    setCloneStartDate(new Date().toISOString().slice(0, 10));
    setCloneOverrides({ role: '', weekly_contractual_hours: '', color: '', comments: '' });
    setCloneDialogOpen(true);
  };

  const handleCloneUser = async () => {
    if (!cloneSourceUserId) {
      toast.error('Sélectionnez un collaborateur à cloner');
      return;
    }
    if (!cloneStartDate) {
      toast.error('Indiquez la date de reprise');
      return;
    }
    const source = users.find((u) => u.id === cloneSourceUserId);
    const confirmMsg = `Cloner ${source?.first_name} ${source?.last_name} à partir du ${cloneStartDate} ?\n\nLe compte actuel sera renommé avec "_old" et désactivé.`;
    if (!window.confirm(confirmMsg)) return;

    setCloneLoading(true);
    try {
      const payload = { start_date: cloneStartDate };
      if (cloneOverrides.role) payload.role = cloneOverrides.role;
      if (cloneOverrides.weekly_contractual_hours) payload.weekly_contractual_hours = parseFloat(cloneOverrides.weekly_contractual_hours);
      if (cloneOverrides.color) payload.color = cloneOverrides.color;
      if (cloneOverrides.comments) payload.comments = cloneOverrides.comments;

      await axios.post(`${API}/users/${cloneSourceUserId}/clone`, payload);
      toast.success('Collaborateur cloné avec succès');
      setCloneDialogOpen(false);
      fetchUsers();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors du clonage'));
    } finally {
      setCloneLoading(false);
    }
  };

  const openSimDialog = () => {
    setSimUserId('');
    setSimStartDate(new Date().toISOString().slice(0, 10));
    setSimResult(null);
    setApplying({ hours: false, leave: false });
    setSimDialogOpen(true);
  };

  const runSimulation = async () => {
    if (!simUserId) {
      toast.error('Sélectionnez un collaborateur');
      return;
    }
    if (!simStartDate) {
      toast.error('Sélectionnez une date de démarrage');
      return;
    }
    setSimLoading(true);
    setSimResult(null);
    try {
      const res = await axios.get(`${API}/users/${simUserId}/balance-simulation`, {
        params: { start_date: simStartDate }
      });
      setSimResult(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur lors de la simulation'));
    } finally {
      setSimLoading(false);
    }
  };

  const applyBalanceAdjustment = async (type) => {
    if (!simResult) return;
    const isHours = type === 'hours';
    const label = isHours ? 'l\'ajustement des heures' : 'le prorata de congés';
    const confirmMsg = isHours
      ? `Appliquer +${simResult.due_hours_ytd}h d'ajustement au ${simStartDate} ?`
      : `Écraser l'allocation congés ${simResult.year} à ${simResult.prorated_leave_days}j (${simResult.prorated_leave_hours}h) ?`;
    if (!window.confirm(confirmMsg)) return;

    setApplying({ ...applying, [type]: true });
    try {
      const payload = {
        start_date: simStartDate,
        apply_hours: isHours,
        apply_leave: !isHours,
      };
      if (isHours) payload.hours_value = simResult.due_hours_ytd;
      else payload.leave_hours_value = simResult.prorated_leave_hours;

      await axios.post(`${API}/users/${simUserId}/balance-adjustment`, payload);
      toast.success(`Application réussie de ${label}`);
      // Refresh simulation to reflect new state
      await runSimulation();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Erreur d\'application'));
    } finally {
      setApplying({ ...applying, [type]: false });
    }
  };

  const openNewDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl">{t('userManagement').toUpperCase()}</h1>
          <p className="text-muted-foreground text-sm">
            {users.length} {t('usersRegistered')}
          </p>
        </div>
        
        <div className="flex flex-col md:flex-row gap-2">
          <Button onClick={openNewDialog} data-testid="new-user-btn">
            <Plus className="h-4 w-4 mr-2" />
            {t('newUser')}
          </Button>
          {user?.role === 'directeur' && (
            <Button
              onClick={openCloneDialog}
              variant="outline"
              data-testid="clone-user-btn"
            >
              <Copy className="h-4 w-4 mr-2" />
              Cloner un collaborateur
            </Button>
          )}
          {(user?.role === 'directeur' || user?.role === 'manager') && (
            <Button
              onClick={openSimDialog}
              variant="outline"
              data-testid="simulate-balance-btn"
            >
              <Calculator className="h-4 w-4 mr-2" />
              Simuler solde initial
            </Button>
          )}
        </div>
      </div>

      {/* Filter for inactive users */}
      <div className="flex items-center gap-2">
        <Checkbox 
          id="show-inactive" 
          checked={showInactiveUsers} 
          onCheckedChange={setShowInactiveUsers}
          data-testid="show-inactive-checkbox"
        />
        <label htmlFor="show-inactive" className="text-sm cursor-pointer">
          {t('showInactiveUsers')}
        </label>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="stat-card">
          <p className="stat-label">{t('collaborators')}</p>
          <p className="stat-value">{users.filter(u => u.role === 'collaborateur').length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">{t('managers')}</p>
          <p className="stat-value">{users.filter(u => u.role === 'manager').length}</p>
        </div>
        <div className="stat-card">
          <p className="stat-label">{t('active')}</p>
          <p className="stat-value">{users.filter(u => u.is_active).length}</p>
        </div>
      </div>

      {/* Users Table */}
      <div className="border border-border rounded-lg overflow-hidden">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('userColor')}</th>
              <th>{t('name')}</th>
              <th>{t('email')}</th>
              <th>{t('role')}</th>
              <th>{t('weeklyContractualHours')}</th>
              <th>{t('defaultLeaveDays')}</th>
              <th>{t('defaultLanguage')}</th>
              <th>{t('status')}</th>
              <th>{t('actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter(targetUser => showInactiveUsers || targetUser.is_active)
              .map((targetUser) => (
              <tr key={targetUser.id} data-testid={`user-row-${targetUser.id}`}>
                <td>
                  <div 
                    className="w-6 h-6 rounded-full border-2 border-white shadow"
                    style={{ backgroundColor: targetUser.color || '#3B82F6' }}
                  />
                </td>
                <td className="font-medium">
                  {targetUser.first_name} {targetUser.last_name}
                </td>
                <td className="text-muted-foreground">{targetUser.email}</td>
                <td>
                  <Badge className={`${ROLE_LABELS[targetUser.role]?.class || 'bg-gray-500'} text-white rounded`}>
                    {ROLE_LABELS[targetUser.role]?.label || targetUser.role}
                  </Badge>
                </td>
                <td className="mono text-sm">
                  {targetUser.weekly_contractual_hours || 40}h
                </td>
                <td className="mono text-sm">
                  {targetUser.default_leave_days || 25}j
                </td>
                <td>
                  <span className="text-sm uppercase">{targetUser.default_language || 'fr'}</span>
                </td>
                <td>
                  {targetUser.is_active ? (
                    <span className="text-emerald-600 flex items-center gap-1">
                      <UserCheck className="h-4 w-4" /> {t('active')}
                    </span>
                  ) : (
                    <span className="text-red-500 flex items-center gap-1">
                      <UserX className="h-4 w-4" /> {t('inactive')}
                    </span>
                  )}
                </td>
                <td>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(targetUser)}
                      className="h-8 px-2"
                      data-testid={`edit-user-${targetUser.id}`}
                      title={t('edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openPasswordDialog(targetUser)}
                      className="h-8 px-2"
                      data-testid={`password-user-${targetUser.id}`}
                      title={t('changePassword')}
                    >
                      <Key className="h-4 w-4 text-blue-500" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleToggleActive(targetUser)}
                      className="h-8 px-2"
                      data-testid={`toggle-user-${targetUser.id}`}
                    >
                      {targetUser.is_active ? (
                        <UserX className="h-4 w-4 text-amber-500" />
                      ) : (
                        <UserCheck className="h-4 w-4 text-emerald-500" />
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingUser ? t('editUser').toUpperCase() : t('newUser').toUpperCase()}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('firstName')}</Label>
                <Input
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  data-testid="user-firstname"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('lastName')}</Label>
                <Input
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  data-testid="user-lastname"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t('email')}</Label>
              <Input
                type="email"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                data-testid="user-email"
              />
            </div>

            {!editingUser && (
              <div className="space-y-2">
                <Label>{t('password')}</Label>
                <Input
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  data-testid="user-password"
                />
              </div>
            )}

            {/* Role selector - always visible for directeur */}
            {user?.role === 'directeur' && (
              <div className="space-y-2">
                <Label>{t('role')}</Label>
                <Select
                  value={newUser.role}
                  onValueChange={(v) => setNewUser({ ...newUser, role: v })}
                >
                  <SelectTrigger data-testid="user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="collaborateur">{t('collaborator')}</SelectItem>
                    <SelectItem value="manager">{t('manager')}</SelectItem>
                    <SelectItem value="directeur">{t('director')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Color Picker */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Palette className="h-4 w-4" />
                {t('userColor')}
              </Label>
              <div className="flex gap-2 flex-wrap">
                {USER_COLORS.map(color => (
                  <button
                    key={color}
                    className={`w-8 h-8 rounded-full transition-all ${newUser.color === color ? 'ring-2 ring-offset-2 ring-primary scale-110' : 'hover:scale-105'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => setNewUser({ ...newUser, color })}
                    type="button"
                  />
                ))}
              </div>
            </div>

            {/* Default Language */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Globe className="h-4 w-4" />
                {t('defaultLanguage')}
              </Label>
              <Select
                value={newUser.default_language}
                onValueChange={(v) => setNewUser({ ...newUser, default_language: v })}
              >
                <SelectTrigger data-testid="user-language">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANGUAGES.map(lang => (
                    <SelectItem key={lang.code} value={lang.code}>{lang.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Weekly Contractual Hours - OBLIGATOIRE */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {t('weeklyContractualHours')} <span className="text-red-500">*</span>
              </Label>
              <Input
                type="number"
                min="1"
                max="60"
                step="0.5"
                value={newUser.weekly_contractual_hours}
                onChange={(e) => setNewUser({ ...newUser, weekly_contractual_hours: parseFloat(e.target.value) || '' })}
                data-testid="user-contractual-hours"
                required
                className={!newUser.weekly_contractual_hours ? 'border-red-500' : ''}
              />
              {!newUser.weekly_contractual_hours && (
                <p className="text-xs text-red-500">{t('requiredField') || 'Champ obligatoire'}</p>
              )}
            </div>

            {/* Default Leave Days */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                {t('defaultLeaveDays')}
              </Label>
              <Input
                type="number"
                min="0"
                max="60"
                step="0.5"
                value={newUser.default_leave_days}
                onChange={(e) => setNewUser({ ...newUser, default_leave_days: parseFloat(e.target.value) || 25 })}
                data-testid="user-default-leave-days"
              />
            </div>

            {/* Dashboard Widget Preferences */}
            <div className="space-y-3 pt-2 border-t border-border">
              <Label className="text-sm font-medium">{t('dashboardWidgets')}</Label>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="show-weather"
                    checked={newUser.show_weather}
                    onCheckedChange={(checked) => setNewUser({ ...newUser, show_weather: checked })}
                    data-testid="user-show-weather"
                  />
                  <label htmlFor="show-weather" className="flex items-center gap-2 text-sm cursor-pointer">
                    <Cloud className="h-4 w-4 text-blue-500" />
                    {t('showWeather')}
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="show-news"
                    checked={newUser.show_news_altstaetten}
                    onCheckedChange={(checked) => setNewUser({ ...newUser, show_news_altstaetten: checked })}
                    data-testid="user-show-news"
                  />
                  <label htmlFor="show-news" className="flex items-center gap-2 text-sm cursor-pointer">
                    <Newspaper className="h-4 w-4 text-amber-500" />
                    {t('showNewsAltstaetten')}
                  </label>
                </div>
                <div className="flex items-center gap-2">
                  <Checkbox 
                    id="show-tennis"
                    checked={newUser.show_tennis}
                    onCheckedChange={(checked) => setNewUser({ ...newUser, show_tennis: checked })}
                    data-testid="user-show-tennis"
                  />
                  <label htmlFor="show-tennis" className="flex items-center gap-2 text-sm cursor-pointer">
                    <Trophy className="h-4 w-4 text-green-500" />
                    {t('showTennis')}
                  </label>
                </div>
              </div>
            </div>

            {/* Hire date + Departure date + Comments */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label htmlFor="user-hire-date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date d&apos;entrée
                </Label>
                <Input
                  id="user-hire-date"
                  type="date"
                  value={newUser.hire_date}
                  onChange={(e) => setNewUser({ ...newUser, hire_date: e.target.value })}
                  data-testid="user-hire-date"
                />
                <p className="text-xs text-muted-foreground">
                  Les heures dues sont calculées à partir de cette date.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-departure-date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Date de sortie
                </Label>
                <Input
                  id="user-departure-date"
                  type="date"
                  value={newUser.departure_date}
                  onChange={(e) => setNewUser({ ...newUser, departure_date: e.target.value })}
                  data-testid="user-departure-date"
                />
                <p className="text-xs text-muted-foreground">
                  Après cette date, les heures dues s&apos;arrêtent.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-comments" className="flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Commentaires
                </Label>
                <Textarea
                  id="user-comments"
                  value={newUser.comments}
                  onChange={(e) => setNewUser({ ...newUser, comments: e.target.value })}
                  placeholder="Notes internes"
                  rows={3}
                  data-testid="user-comments"
                />
              </div>
            </div>

            <Button
              onClick={editingUser ? handleUpdateUser : handleCreateUser}
              className="w-full"
              data-testid="save-user-btn"
            >
              {editingUser ? t('update') : t('createUser')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('changePassword').toUpperCase()}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 pt-4">
            {selectedUserForPassword && (
              <p className="text-sm text-muted-foreground">
                {selectedUserForPassword.first_name} {selectedUserForPassword.last_name}
              </p>
            )}
            
            <div className="space-y-2">
              <Label>{t('newPassword')}</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder={t('newPassword')}
                data-testid="new-password-input"
              />
            </div>

            <Button
              onClick={handleResetPassword}
              className="w-full"
              data-testid="reset-password-btn"
            >
              {t('changePassword')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Clone User Dialog */}
      <Dialog open={cloneDialogOpen} onOpenChange={setCloneDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5" />
              Cloner un collaborateur
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm text-amber-900">
              <p className="font-semibold mb-1">Comment ça marche :</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Le compte source sera renommé avec le suffixe <code>_old</code> et désactivé</li>
                <li>Un nouveau compte est créé avec les mêmes email, mot de passe et profil</li>
                <li>Le planning, les absences et heures supp. à partir de la date choisie sont transférés vers le nouveau compte</li>
                <li>L&apos;historique antérieur reste sur le compte <code>_old</code></li>
              </ul>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clone-source">Collaborateur à cloner *</Label>
              <Select value={cloneSourceUserId} onValueChange={setCloneSourceUserId}>
                <SelectTrigger id="clone-source" data-testid="clone-source-select">
                  <SelectValue placeholder="Sélectionner un collaborateur..." />
                </SelectTrigger>
                <SelectContent>
                  {users
                    .filter((u) => u.is_active)
                    .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))
                    .map((u) => (
                      <SelectItem key={u.id} value={u.id} data-testid={`clone-source-option-${u.id}`}>
                        <span className="flex items-center gap-2">
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: u.color || '#64748B' }}
                          />
                          {u.first_name} {u.last_name}
                          <span className="text-muted-foreground text-xs">({u.email})</span>
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="clone-date">Date de reprise du planning *</Label>
              <Input
                id="clone-date"
                type="date"
                value={cloneStartDate}
                onChange={(e) => setCloneStartDate(e.target.value)}
                data-testid="clone-start-date"
              />
              <p className="text-xs text-muted-foreground">
                Créneaux, absences et heures supp. à partir de cette date seront transférés vers le nouveau compte.
              </p>
            </div>

            <div className="border-t pt-4 space-y-3">
              <p className="text-sm font-semibold text-muted-foreground">Modifications optionnelles (sinon copiées depuis le source)</p>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="clone-role" className="text-xs">Rôle</Label>
                  <Select
                    value={cloneOverrides.role || 'none'}
                    onValueChange={(v) => setCloneOverrides({ ...cloneOverrides, role: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger id="clone-role" data-testid="clone-role-select">
                      <SelectValue placeholder="Inchangé" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Inchangé</SelectItem>
                      <SelectItem value="collaborateur">Collaborateur</SelectItem>
                      <SelectItem value="manager">Manager</SelectItem>
                      <SelectItem value="directeur">Directeur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="clone-hours" className="text-xs">Heures / semaine</Label>
                  <Input
                    id="clone-hours"
                    type="number"
                    step="0.5"
                    min="0"
                    value={cloneOverrides.weekly_contractual_hours}
                    onChange={(e) => setCloneOverrides({ ...cloneOverrides, weekly_contractual_hours: e.target.value })}
                    placeholder="Inchangé"
                    data-testid="clone-hours-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clone-color" className="text-xs flex items-center gap-2">
                  <Palette className="h-3 w-3" />
                  Couleur (optionnel)
                </Label>
                <div className="flex flex-wrap gap-1">
                  <button
                    type="button"
                    onClick={() => setCloneOverrides({ ...cloneOverrides, color: '' })}
                    className={`w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs ${cloneOverrides.color === '' ? 'border-primary ring-2 ring-primary' : 'border-transparent'}`}
                    title="Inchangé"
                  >
                    —
                  </button>
                  {USER_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setCloneOverrides({ ...cloneOverrides, color: c })}
                      className={`w-7 h-7 rounded-full border-2 ${cloneOverrides.color === c ? 'border-primary ring-2 ring-primary' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="clone-comments" className="text-xs">Commentaires (optionnel)</Label>
                <Textarea
                  id="clone-comments"
                  value={cloneOverrides.comments}
                  onChange={(e) => setCloneOverrides({ ...cloneOverrides, comments: e.target.value })}
                  placeholder="Notes sur le nouveau compte"
                  rows={2}
                  data-testid="clone-comments"
                />
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setCloneDialogOpen(false)} disabled={cloneLoading}>
                Annuler
              </Button>
              <Button
                onClick={handleCloneUser}
                disabled={cloneLoading || !cloneSourceUserId || !cloneStartDate}
                data-testid="confirm-clone-btn"
              >
                {cloneLoading ? 'Clonage...' : 'Cloner'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Simulate Initial Balance Dialog */}
      <Dialog open={simDialogOpen} onOpenChange={setSimDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Simulation solde initial
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="p-3 rounded-md bg-blue-50 border border-blue-200 text-sm text-blue-900">
              <p className="font-semibold mb-1">Objectif :</p>
              <p className="text-xs">
                Pour un collaborateur qui commence en cours d&apos;année, calcule 1) l&apos;ajustement d&apos;heures à créer pour que le solde d&apos;heures parte de 0 à la date d&apos;entrée, et 2) le prorata de congés jusqu&apos;au 31/12.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="sim-user">Collaborateur *</Label>
                <Select value={simUserId} onValueChange={(v) => { setSimUserId(v); setSimResult(null); }}>
                  <SelectTrigger id="sim-user" data-testid="sim-user-select">
                    <SelectValue placeholder="Choisir..." />
                  </SelectTrigger>
                  <SelectContent>
                    {users
                      .sort((a, b) => (a.last_name || '').localeCompare(b.last_name || ''))
                      .map((u) => (
                        <SelectItem key={u.id} value={u.id} data-testid={`sim-user-option-${u.id}`}>
                          <span className="flex items-center gap-2">
                            <span
                              className="inline-block w-2 h-2 rounded-full"
                              style={{ backgroundColor: u.color || '#64748B' }}
                            />
                            {u.first_name} {u.last_name}
                            {!u.is_active && <span className="text-xs text-muted-foreground">(inactif)</span>}
                          </span>
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="sim-date">Date d&apos;entrée *</Label>
                <Input
                  id="sim-date"
                  type="date"
                  value={simStartDate}
                  onChange={(e) => { setSimStartDate(e.target.value); setSimResult(null); }}
                  data-testid="sim-start-date"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button
                onClick={runSimulation}
                disabled={simLoading || !simUserId || !simStartDate}
                data-testid="run-simulation-btn"
              >
                {simLoading ? 'Calcul...' : 'Simuler'}
              </Button>
            </div>

            {simResult && (
              <div className="border rounded-lg divide-y" data-testid="sim-result">
                {/* Info user */}
                <div className="p-3 bg-slate-50 text-xs text-slate-700 space-y-1">
                  <p><span className="font-semibold">{simResult.user_name}</span> · Heures contract. : <span className="font-mono">{simResult.weekly_contractual_hours}h/sem</span> · Congés annuels : <span className="font-mono">{simResult.default_leave_days}j</span></p>
                  <p>Jours ouvrés {simResult.year} : <span className="font-mono">{simResult.annual_working_days}j</span> (dont <span className="font-mono">{simResult.working_days_ytd}j</span> avant la date d&apos;entrée, <span className="font-mono">{simResult.remaining_working_days}j</span> après)</p>
                </div>

                {/* Row 1: Hours adjustment */}
                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">Heures théoriquement dues du 01/01 → {simStartDate}</p>
                    <p className="text-2xl font-mono text-slate-800" data-testid="sim-due-hours">
                      +{simResult.due_hours_ytd.toFixed(2)} h
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      À créer comme ajustement heures supp. positif à la date d&apos;entrée pour repartir de 0
                    </p>
                  </div>
                  <Button
                    onClick={() => applyBalanceAdjustment('hours')}
                    disabled={applying.hours || simResult.due_hours_ytd === 0}
                    data-testid="apply-hours-btn"
                    className="shrink-0"
                  >
                    {applying.hours ? 'Application...' : 'Appliquer les heures'}
                  </Button>
                </div>

                {/* Row 2: Leave allocation */}
                <div className="p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">Congés à acquérir du {simStartDate} → 31/12</p>
                    <p className="text-2xl font-mono text-slate-800" data-testid="sim-leave-days">
                      {simResult.prorated_leave_days.toFixed(1)} j
                      <span className="text-sm ml-2 text-muted-foreground">({simResult.prorated_leave_hours.toFixed(2)} h)</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Prorata: {simResult.default_leave_days}j × ({simResult.remaining_working_days}/{simResult.annual_working_days} jours ouvrés)
                      {simResult.existing_allocated_hours > 0 && (
                        <span className="block mt-1 text-amber-700">
                          Allocation actuelle : {simResult.existing_allocated_hours}h → sera <b>écrasée</b>
                        </span>
                      )}
                    </p>
                  </div>
                  <Button
                    onClick={() => applyBalanceAdjustment('leave')}
                    disabled={applying.leave}
                    data-testid="apply-leave-btn"
                    className="shrink-0"
                  >
                    {applying.leave ? 'Application...' : 'Appliquer les congés'}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => setSimDialogOpen(false)}>
                Fermer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
